/**
 * Release notes, newest first: one array per month, keyed by year then month.
 * Each release starts with a { releaseDate } entry, followed by { content } entries.
 *
 * Optional: add `highlights` to the releaseDate entry to feature them in the dashboard
 * "What's new" card. Only the newest release's highlights are shown, and each new
 * releaseDate brings the card, the Release Notes dot and the "New" nav tags back for everyone.
 */
export interface ReleaseHighlight {
  /** Unique within the release. */
  id: string;
  title: string;
  /** One short line a first-time user can follow. */
  summary: string;
  tag: 'New' | 'Beta' | 'Improved';
  /** Clarity shape. Register new shapes in dashboard.component.ts, or it renders as "...". */
  icon: string;
  /** Adds an Open button, and a "New" tag on that nav tab until the user visits it. */
  route?: '/quick-sizer' | '/chat' | '/ai-assessment';
  /** Feature flag the user needs; omit for features everyone can use. */
  flag?: 'AI_ASSISTANT' | 'AI_ASSESSMENT';
  /** Drop the row once the user has opened its route. */
  hideAfterVisit?: boolean;
}

export interface ReleaseNoteEntry {
  releaseDate?: string;
  highlights?: ReleaseHighlight[];
  content?: string;
  subTitle?: string;
}

const oct_2026: ReleaseNoteEntry[] = [
  {
    releaseDate: "October 22nd, 2026",
    highlights: [
      {
        id: 'quick-sizer',
        title: 'Quick Sizer',
        summary: 'Enter your host count and hardware to see how many VMs they can run.',
        tag: 'New',
        icon: 'calculator',
        route: '/quick-sizer',
        hideAfterVisit: true,
      },
      {
        id: 'assistant',
        title: 'Assistant',
        summary: 'Describe your workloads in plain English and get a sizing recommendation.',
        tag: 'Beta',
        icon: 'chat-bubble',
        route: '/chat',
        flag: 'AI_ASSISTANT',
      },
      {
        id: 'assessment',
        title: 'Assessment',
        summary: 'Upload an RVTools or Live Optics file for an AI review of your current VMs.',
        tag: 'Beta',
        icon: 'rack-server',
        route: '/ai-assessment',
        flag: 'AI_ASSESSMENT',
      },
      {
        id: 'vsan-storage-clusters',
        title: 'vSAN Storage Cluster Sizing',
        summary: 'Size a vSAN cluster that only provides storage to your compute clusters.',
        tag: 'New',
        icon: 'storage',
      },
      {
        id: 'shared-clusters',
        title: 'Multiple Workloads on a Single Cluster',
        summary: 'Put several workloads on one cluster and size them together.',
        tag: 'New',
        icon: 'host-group',
      },
      {
        id: 'resource-utilization',
        title: 'Customizable Resource Utilization',
        summary: 'Tune resource utilization on the recommendation page; sizing recalculates.',
        tag: 'New',
        icon: 'slider',
      },
    ],
  },
  {
    content: `
      <h2>Private Cloud Sizer Release Notes</h2>

      <p>This release adds sizing for vSAN Storage Clusters, where a separate vSAN storage cluster provides storage to one or more compute clusters. Several workloads can now share one physical cluster. It also introduces Quick Sizer, a fast way to see how many VMs a cluster can run after accounting for vSAN and VCF management overheads.</p>

      <h3>Key Features</h3>
      <ul class="list">
        <li><strong>vSAN Storage Cluster Sizing:</strong> Workloads can now be sized as storage-only clusters (SON) that serve storage to compute-only clusters (CON), alongside traditional HCI.
          <ul class="list">
            <li><strong>Workload-Driven Sizing:</strong> Users define workloads on the compute side. The sizer aggregates capacity and IOPS from every connected compute cluster and recommends the storage cluster details: host count, ReadyNode profile, raw and usable capacity, and IOPS headroom.</li>
            <li><strong>Supported Topologies:</strong>
              <ul class="list">
                <li>One compute cluster connected to one storage cluster.</li>
                <li>Many compute clusters connected to one storage cluster.</li>
                <li>The same layouts across workload domains within one VCF instance.</li>
                <li>An HCI cluster that also mounts a storage cluster.</li>
                <li>One compute cluster connected to several storage clusters.</li>
              </ul>
            </li>
            <li><strong>Cross-Workload-Domain Mapping:</strong> A storage cluster can serve compute clusters in other workload domains.</li>
            <li><strong>Automatic Cluster Splitting:</strong> Some requests would exceed the 24-host limit for a storage cluster or the 128-host limit for a storage cluster plus its compute clusters. The sizer splits these recommendations into valid clusters. Depending on which limit is reached, it creates separate compute-plus-storage groups, more compute clusters on one storage cluster, or more storage clusters behind one compute cluster.</li>
            <li><strong>Storage Cluster Options:</strong> vSAN ESA on VCF 9.0 and 9.1 with auto-RAID and manual RAID selections. Users can set deduplication, compression, encryption, data reduction ratio, read/write ratio and IO size.</li>
            <li><strong>Network Sizing:</strong> With traffic separation enabled, North-South and East-West network speeds are entered or recommended separately. Without it, there is a single network speed. When no hardware is chosen, speeds are recommended from the Broadcom Compatibility Guide (BCG).</li>
            <li><strong>Stretched Cluster Support:</strong> Storage clusters can be stretched across two sites. A stretched compute cluster always gets a stretched storage cluster. A compute cluster that is not stretched can still mount a stretched storage cluster. Stretched compute clusters have no witness.</li>
            <li><strong>Dedicated Recommendation Views:</strong> Separate views for storage clusters and compute clusters, including overhead breakdowns and the list of connected compute clusters. Warnings are shown for cross-domain mappings, host limits and stretched-cluster failure cases.</li>
          </ul>
        </li>
        <li><strong>Multiple Workloads on a Single Cluster:</strong>
          <ul class="list">
            <li>Several workloads can now share one physical HCI or compute cluster by picking the same cluster name with "Use Existing Cluster." Their compute and memory demands are sized together as one cluster.</li>
            <li>An HCI workload and a compute-only workload in the same workload domain can share one cluster. The HCI workload keeps its storage on that cluster, and the compute-only workload's storage stays on its storage cluster.</li>
            <li>Storage clusters can be shared across workload domains. HCI and compute cluster sharing stays within one workload domain.</li>
            <li>Each recommended cluster lists every workload that shares it.</li>
          </ul>
        </li>
        <li><strong>Quick Sizer:</strong> Enter a host count and hardware to see how many VMs the cluster can run after vSAN and VCF management overheads. Quick Sizer supports VCF 9.1.0 with vSAN ESA.
          <ul class="list">
            <li><strong>Simple Inputs:</strong> Four steps: Cluster Setup, Hardware Configuration, Data Protection and VCF Components. Hardware can be picked through a vSAN ReadyNode profile or a CPU series.</li>
            <li><strong>Single Recommendation Screen:</strong> One view that shows what the cluster can run.</li>
            <li><strong>VM Capacity by Size:</strong> How many VMs fit for each VM size, and which resource runs out first.</li>
            <li><strong>Available Resources:</strong> Storage, memory, CPU cores and IOPS left for workloads.</li>
            <li><strong>Management Domain:</strong> Resources set aside for VCF management components, with their brick sizes.</li>
            <li><strong>Storage Capacity Breakdown:</strong> A step-by-step view of how raw storage becomes the storage available for workloads.</li>
            <li><strong>vSAN Capacity Licensing Alert:</strong> Shows a warning when the installed storage needs more vSAN capacity licensing.</li>
            <li><strong>PDF Export:</strong> The recommendation can be downloaded as a PDF with the "Export to PDF" button.</li>
          </ul>
        </li>
        <li><strong>Customizable Resource Utilization:</strong> You can now adjust resource utilization up or down using the UI controls in the Resource Utilization Bar Graph section of the HCI Workload Details Recommendation Page. Changing these settings automatically recalculates the resource recommendation for that workload.</li>
      </ul>

      <h3>Sizing Calculation Updates</h3>
      <ul class="list">
        <li><strong>vSAN Calculation Updates:</strong> vSAN sizing calculations have been updated in this release. Projects sized in an earlier release may show slightly different host counts, storage capacity and vSAN overheads when they are sized again. These differences are expected and reflect the updated calculations.</li>
      </ul>

      <h3>What's Next</h3>
      <ul class="list">
        <li><strong>Consolidated Architecture:</strong> Support sizing deployments where management components and customer workloads share the Management Domain infrastructure.</li>
        <li><strong>Management Domain-Only Sizing:</strong> Size the infrastructure required for a standalone Management Domain without adding workload domains or customer workloads.</li>
        <li><strong>Smart Skipping for Management Domain Configuration:</strong> Streamline the sizing workflow by reusing an existing valid Management Domain configuration and skipping repeated configuration steps.</li>
        <li><strong>Memory Tiering Support:</strong> Extend sizing to account for memory tiering when evaluating host memory capacity and workload requirements.</li>
        <li><strong>External Storage Support in the Management Domain:</strong> Expand Management Domain sizing to include external storage options alongside vSAN.</li>
      </ul>
     `
  }
]

const sep_2026 = [
  {
    releaseDate: "September 9th, 2026"
  },
  {
    content: `
      <h2>Private Cloud Sizer Release Notes</h2>

      <p>This release introduces the Workload-First Sizing methodology without hardware constraints, a streamlined configuration UI across the Add Workload and Management Domain sections, and a complete UX overhaul of output components featuring tree-based navigation for enhanced data visibility and clarity.</p>

      <h3>Key Features</h3>
      <ul class="list">
        <li><strong>Workload-First Sizing (No Hardware Constraints Required):</strong> The system automatically sizes infrastructure for HCI profiles and provides recommendations without requiring prior hardware selection. Users can optionally select a ReadyNode profile at the output stage to customize and compare against system-generated recommendations for each profile. Selecting a specific ReadyNode or CPU upfront remains available as an optional input setting.</li>
        <li><strong>Simplified Add Workload & Management Domain Configuration:</strong> Reorganized and aligned input sections to improve usability and streamline layout flow, making configuration input and setup navigation much easier.</li>
        <li><strong>Complete UX Overhaul for Output Components:</strong> Replaced the legacy tab-based view with an intuitive, hierarchical tree navigation structure:
          <ul class="list">
            <li><strong>Tree Navigation:</strong> Easily navigate output components without visual clutter or space constraints.</li>
            <li><strong>Expandable Components:</strong> View all available components in a single consolidated tree, eliminating the need to toggle between tabs.</li>
            <li><strong>Cleaner Data Representation:</strong> Visual and structural improvements deliver better readability and clearer data organization for sizing recommendations.</li>
          </ul>
        </li>
        <li><strong>Free-Form ReadyNode Search:</strong> Enhanced search capabilities to allow flexible, free-form searching for ReadyNode profiles.</li>
      </ul>

      <h3>What's Next</h3>
      <ul class="list">
        <li>VCF Quick Sizing.</li>
        <li>vSAN MAX Sizing.</li>
        <li>Management Domain Sizing Enhancements.</li>
      </ul>
     `
  }
]

const aug_2026 = [
  {
    releaseDate: "August 19th, 2026"
  },
  {
    content: `
      <h2>Private Cloud Sizer Release Notes</h2>

      <p>This release adds a new Small, Non-HA brick size for NSX Manager, giving users a lighter-weight sizing option for Management Domain deployments that don't require a highly-available NSX Manager cluster.</p>

      <h3>Key Features</h3>
      <ul class="list">
        <li><strong>NSX Manager Small (Non-HA) Brick Size:</strong> Management Domain sizing for VCF 9.0 and 9.1 now offers a Small, Non-HA brick size for NSX Manager, a single-VM, non-clustered deployment option alongside the existing Medium, Large, and X-Large HA cluster tiers.</li>
      </ul>
     `
  },
  {
    releaseDate: "August 10th, 2026"
  },
  {
    content: `
      <h2>Private Cloud Sizer Release Notes</h2>

      <p>This release adds configurable oversubscription controls to Management Domain sizing, giving users finer-grained control over compute and memory capacity planning.</p>

      <h3>Key Features</h3>
      <ul class="list">
        <li><strong>Configurable vCPU/pCore and Memory Overcommit Ratios:</strong> Management Domain Sizing now supports configurable vCPU/pCore and Memory Overcommit ratios (1–4) for CPU and memory oversubscription.</li>
      </ul>
     `
  }
]

const jul_2026 = [
  {
    releaseDate: "July 21st, 2026"
  },
  {
    content: `
      <h2>Private Cloud Sizer Release Notes</h2>

      <p>Private Cloud Sizer is now Generally Available. This release also includes a correction to Stretched Cluster sizing to align with vSAN's supported configurations, along with a fix to Isolated Workload Domain sizing.</p>

      <h3>Key Features</h3>
      <ul class="list">
        <li><strong>General Availability:</strong> Private Cloud Sizer is now Generally Available; the Beta label has been removed from the tool.</li>
      </ul>

      <h3>Bug Fixes</h3>
      <ul class="list">
        <li><strong>Stretched Cluster Storage Reserve Inputs:</strong> Stretched Cluster sizing no longer allows vSAN Operations Reserve or Host Rebuild Reserve inputs, since vSAN doesn't support them in stretched configurations.</li>
        <li><strong>Isolated Workload Domain Management Component Placement:</strong> Isolated Workload Domain sizing now correctly places vCenter and NSX Manager sizing within the Management Domain.</li>
      </ul>
     `
  }
]

const jun_2026 = [
  {
    releaseDate: "June 29th, 2026"
  },
  {
    content: `
      <h2>Private Cloud Sizer Release Notes</h2>

      <p>This release introduces collaboration workflow improvements, including an idle-session unlock prompt and self-share prevention, alongside targeted bug fixes to enhance ReadyNode reliability and owner email resolution accuracy.</p>

      <h3>Collaboration Feature Enhancements</h3>
      <ul class="list">
        <li><strong>Unlock &amp; Exit Button:</strong> Project owners with an active lock can now release it directly from the project editor via the new "Unlock & exit" button, making the project immediately available for others to edit.</li>
        <li><strong>Idle Session Prompt:</strong> After 60 minutes of inactivity on a locked project, a modal will prompt the user to either continue their session or unlock the project and return to the dashboard, preventing unnecessary access delays for collaborators.</li>
        <li><strong>Self-Share Prevention:</strong> The sharing form now includes real-time validation to block project owners from adding themselves as collaborators, providing an immediate inline error message if this is attempted.</li>
      </ul>

      <h3>Bug Fixes</h3>
      <ul class="list">
        <li><strong>Shared Icon Visibility:</strong> Resolved an issue where the shared-project cloud icon incorrectly appeared on projects with no active collaborators due to an empty sharing history array.</li>
        <li><strong>Owner Email Resolution:</strong> Fixed an issue causing an incorrect collaborator list when the creator's stored identity was a username rather than a full email; the system now correctly resolves the owner's email from the  currentOwner  field.</li>
        <li><strong>NSX Subgroup Label Typo:</strong> Corrected a labeling error in VCF 9.0 and 9.1 workload configurations where "DeployVMware NSX Manager" and "DeployVMware NSX Edge" were missing a space.</li>
        <li><strong>Workload Domain Output Chart Label:</strong> The resource utilization chart in the workload domain output now correctly labels the storage axis as "Storage" instead of "Capacity."</li>
        <li><strong>ReadyNode Filter State on Revisit:</strong> Fixed an issue where returning to the management domain configuration page caused ReadyNode profile, server model, and generation dropdowns to appear empty due to stale state from previous visits.</li>
        <li><strong>Sharing Form Initialization:</strong> Fixed a form initialization error where the recipient field defaulted to an empty string instead of an empty array, which caused validation failures when opening the share project modal.</li>
        <li><strong>Pending Unlock Retry:</strong> The dashboard now automatically retries project unlock operations that failed in a prior session due to expired authentication tokens, ensuring locks from interrupted sessions are cleared upon the next login.</li>
        <li><strong>Renaming Software Component:</strong> Renaming the Component from VCF Operations Collector to Cloud Proxy.</li>
        <li><strong>vCenter Storage Values:</strong> Updated vCenter storage values across all brick sizes to align with <a href="https://techdocs.broadcom.com/us/en/vmware-cis/vsphere/vsphere/9-1/vcenter-installation-and-setup/deploying-the-vcenter-server-appliance/vcenter-server-appliance-requirements/vcsa-storage-requirements.html" target="_blank">VMware Official Documentation</a>.</li>
      </ul>
     `
  }
]

const may_2026 = [
  {
    releaseDate: "June 11th, 2026"
  },
  {
    content: `
      <h2>Private Cloud Sizer Release Notes</h2>

      <p>This release delivers enhancements to storage efficiency configuration for vSAN ESA stretched clusters, improved default behaviors for VCF 9.0+ workload domains, an updated ReadyNode alert notification for legacy ESA projects, and several bug fixes to improve sizing accuracy and reliability.</p>

      <h3>New Features</h3>
      <ul class="list">
        <li><strong>vSAN ESA Cluster-Type Dependent Storage Efficiency:</strong> The system now automatically configures CompressionOnly space efficiency for stretched clusters running on VCF 9.1+.</li>
        <li><strong>Isolated Workload Domains by Default:</strong> Isolated Workload Type is now set as the standard default choice during VCF 9.0+ workload domain creation workflows.</li>
        <li><strong>Recommendation Page Feedback Trigger:</strong> Added a dedicated feedback mechanism per project directly on the recommendation page to seamlessly gather user insights.</li>
        <li><strong>ESA ReadyNode Alert Banner:</strong> Introduced an alert notification for legacy vSAN ESA 5.2.1 projects created between December 16, 2025, and June 8, 2026, to highlight updated ReadyNode profiles and performance numbers.</li>
      </ul>

      <h3>Bug Fixes</h3>
      <ul class="list">
        <li><strong>Hardware Configuration Dropdown:</strong> Resolved an issue where the ReadyNode dropdown would appear empty after a user switched their hardware configurations.</li>
        <li><strong>Profile Persistence:</strong> Fixed a bug where the recommended profile name was not being properly retained.</li>
        <li><strong>Payload Accuracy:</strong> Corrected a missing data issue where user-defined vSAN OSA disk groups were excluded from the final payload.</li>
        <li><strong>Component Sizing Defaults:</strong> Addressed a configuration error that caused the NSX Manager brick size to default incorrectly to Medium.</li>
      </ul>
     `
  }
]

const apr_2026 = [
  {
    releaseDate: "April 27th, 2026"
  },
  {
    content: `
      <h2>Private Cloud Sizer Release Notes</h2>

      <p>This release introduces comprehensive VMware Cloud Foundation 9.1.0 support with enhanced vSAN ESA deduplication capabilities, Live Optics Project ID import functionality, and numerous critical bug fixes to improve sizing accuracy and user experience.</p>

      <h3>Key Features</h3>
      <ul class="list">
        <li><strong>VCF 9.1.0 Support:</strong> Private Cloud Sizer now fully supports VMware Cloud Foundation 9.1.0 with enhanced storage efficiency features and updated component configurations.
          <ul class="list">
            <li><strong>vSAN ESA Deduplication Support:</strong> Introduction of global deduplication capabilities for vSAN ESA architecture in VCF 9.1.0+. Users can now configure deduplication ratios and view deduplication metadata overhead in sizing recommendations.</li>
            <li><strong>Storage Efficiency Preference:</strong> New storage efficiency preference controls for VCF 9.1.0 ESA deployments, allowing users to optimize storage configurations based on workload characteristics.</li>
            <li><strong>Dedup Metadata Overhead Calculations:</strong> Comprehensive calculation and display of deduplication-related metadata overhead specifically for vSAN ESA deployments in VCF 9.1.0+.</li>
          </ul>
        </li>
        <li><strong>Live Optics Project ID Import:</strong> Streamlined data ingestion workflow with direct Project ID import capabilities.
          <ul class="list">
            <li><strong>Project ID Import Interface:</strong> New input interface allowing users to directly import Live Optics data using numeric Project IDs.</li>
            <li><strong>Seamless Integration:</strong> Direct API integration with Live Optics platform for real-time data retrieval and workload characteristic import.</li>
          </ul>
        </li>
      </ul>

      <h3>Bug Fixes</h3>
      <ul class="list">
        <li><strong>Management Domain Fixes:</strong>
          <ul class="list">
            <li>Fixed component aggregation and rendering issues in stretched cluster configurations.</li>
            <li>Fixed cores and clockSpeed visual styling consistency between workload and management domain configurations in CPU-based sizing mode.</li>
          </ul>
        </li>
        <li><strong>Workload Domain Fixes:</strong>
          <ul class="list">
            <li>Fixed empty component sections display in workload domain summary tab.</li>
            <li>Resolved deduplication value defaulting to 1 for VCF 9.1.0 ESA configurations.</li>
            <li>Fixed dedup metadata overhead calculations and display in workload domain output components.</li>
          </ul>
        </li>
      </ul>

      <h3>Technical Improvements</h3>
      <ul class="list">
        <li><strong>Performance Optimizations:</strong> Various performance improvements in component rendering and API interaction patterns.</li>
      </ul>

      <h3>What's Next</h3>
      <ul class="list">
        <li>Introducing ReadyNode guided sizing for vSAN OSA architecture starting with VCF 5.2.1 .</li> 
        <li>Extending ReadyNode guided sizing for vSAN ESA architecture to support VCF 5.2.1 (previously available from VCF 9.0+).</li>
      </ul>
     `
  }
]

const mar_2026 = [
  {
    releaseDate: "March 31st, 2026"
  },
  {
    content: `
      <h2>Private Cloud Sizer Release Notes</h2>

      <p>This release introduces VCF 9.0 ReadyNode guided sizing, enabling users to map infrastructure recommendations to certified vSAN ReadyNode hardware profiles for accurate Bill of Materials generation.</p>

      <h3>Key Features</h3>
      <ul class="list">
        <li><strong>VCF 9.0 ReadyNode Guided Sizing:</strong> Private Cloud Sizer now supports hardware-aware sizing through certified vSAN ReadyNode profiles for VCF 9.0 ESA (Express Storage Architecture) deployments.
          <ul class="list">
            <li><strong>ReadyNode Selection:</strong> Users can select specific vSAN ReadyNode hardware configurations including vendor, profile, server model, and CPU generation options to align sizing recommendations with actual hardware capabilities.</li>
            <li><strong>Dynamic Hardware Constraints:</strong> The tool automatically applies hardware-specific constraints including CPU core limits, memory options, storage device selections, and network interface configurations to provide accurate hardware-constrained recommendations.</li>
            <li><strong>Filter Persistence:</strong> ReadyNode filter selections are automatically saved with projects and restored upon reopening for improved workflow continuity.</li>
            <li><strong>Auto-Search on Filter Changes:</strong> Immediate search results triggered upon any filter change (vendor, profile, server model, generation) to streamline the hardware selection process.</li>
            <li><strong>Cascading Filter Dependencies:</strong> Intelligent filter cascading ensures that dependent filter options (e.g., server model and generation) are automatically disabled when prerequisite filters (e.g., vendor and profile) are not selected.</li>
          </ul>
        </li>
        <li><strong>Workload-Aware Space Efficiency:</strong> Support the Workload-Aware Space Efficiency model, where workload type specific space efficiency ratios are applied. This sizing feature reduces host count and prevents capacity overestimation.</li>
        <li><strong>Support for Customer search via account id:</strong> Supports the Customer search via account id during the project creation.</li>
      </ul>

      <h3>What's Next</h3>
      <ul class="list">
        <li>Live Optics Project id import support for sizing recommendations.</li>
        <li>CPU model customization for CPU-based sizing.</li>
      </ul>
     `
  },
  {
    releaseDate: "March 18th, 2026"
  },
  {
    content: `
      <h2>Private Cloud Sizer Release Notes</h2>

      <p>This release introduces major enhancements, including VCF 9.0 sizing and stretched cluster sizing capabilities, to provide comprehensive infrastructure recommendations for advanced VMware Cloud Foundation deployments.</p>

      <h3>Key Features</h3>
      <ul class="list">
        <li><strong>VCF 9.0 Sizing:</strong> Private Cloud Sizer now supports VMware Cloud Foundation 9.0 sizing.
          <ul class="list">
            <li><strong>Management Domain Components for VCF 9.0:</strong> Added support for new and updated components including Identity Broker, VCF Operations for Logs, VCF Operations for Network (VRNI Platform and Collector), VCF Automation, and AVI Controller. Enhanced component sizing options with additional tiers for VCF Operations, Network Insights, and Automation components.</li>
            <li><strong>Workload Domain Components for VCF 9.0:</strong> Integrated AVI Service Engine (AVI-SE) sizing for workload domains and added AVI Controller dependency management. Also, added support for HCX component sizing in VCF 9.0 workload domains.</li>
          </ul>
        </li>
        <li><strong>Stretched Cluster Sizing:</strong> Introduced comprehensive support for stretched cluster architectures, enabling users to size both management and workload domains. This includes cluster type selection (Standard vs. Stretched) and updated UI components to display stretched cluster topology, including data and witness site details.</li>
        <li><strong>Report Download:</strong> PowerPoint report downloads now support VCF 9.0 sizing and stretched cluster configurations.</li>
        <li><strong>CPU Headroom:</strong> Updates to CPU Headroom considerations for steady state and failure scenarios.</li>
      </ul>

      <h3>What's Next</h3>
      <ul class="list">
        <li>Support the Workload-Aware Space Efficiency model, where workload type specific space efficiency ratios are applied. This sizing feature reduces host count and prevents capacity overestimation.</li>
      </ul>
     `
  },
  {
    releaseDate: "March 3rd, 2026"
  },
  {
    content: `
      <h2>Private Cloud Sizer Release Notes</h2>

      <p>This release includes a storage calculation optimization to improve the accuracy of infrastructure recommendations.</p>

      <h3>Key Features</h3>
      <ul class="list">
        <li><strong>Storage Calculation Optimization:</strong> The 20% free capacity reservation is now selectively deducted from applicable hardware resources instead of being applied every time. This may result in fewer hosts being recommended for certain storage heavy workloads due to more precise capacity deduction.</li>
      </ul>

      <h3>What's Next</h3>
      <ul class="list">
        <li>Support for Stretched Cluster sizing.</li>
        <li>VCF 9.0 sizing support.</li>
      </ul>
     `
  }
]

const jan_2026 = [
  {
    releaseDate: "January 30th, 2026"
  },
  {
    content: `
      <h2>Private Cloud Sizer Release Notes</h2>

      <p>This release includes customer access expansion and a comprehensive re-architecture of the vSAN OSA sizing engine to deliver improved accuracy and reliability.</p>

      <h3>Key Features</h3>
      <ul class="list">
        <li><strong>Customer Access:</strong> Private Cloud Sizer is now open to customers.</li>
        <li><strong>vSAN OSA Sizing Re-Architecture:</strong> The vSAN OSA (Original Storage Architecture) sizing has been re-architected for improved accuracy, maintainability, and consistency. Key improvements include enhanced memory and IO calculations, optimized disk group management, and several critical bug fixes addressing calculation accuracy for VDI workloads, sequential access patterns, and large cluster formations.
          <ul class="list">
            <li><strong>Memory Calculations Enhancement:</strong> We have enhanced the memory calculations where the memory-related overheads are now correctly subtracted from the server capacity instead of being added to workload memory requirements. This may result in an increase in host count for memory-intensive workloads when the Server RAM is low, as the available memory per host is now calculated more accurately.</li>
            <li><strong>Disk group and drive value derivation:</strong> The sizing engine now consistently uses values derived from calculations without checking ReadyNode profile characteristics. Profile inference and application of profile-specific characteristics will be introduced in a future release where users can explicitly select ReadyNode profiles.</li>
            <li><strong>Large Cluster Optimization (>64 Hosts):</strong> The cluster formation algorithm has been optimized to minimize host count inflation when splitting large clusters.</li>
            <li><strong>Sequential Access Pattern - Bandwidth Calculation:</strong> We have optimised the bandwidth calculations by picking accurate performance numbers.</li>
            <li><strong>VDI Workload - Persistent Disk Size Correction:</strong> The persistent disk size overhead calculation has been optimised to properly account for the total overhead across all VMs.</li>
          </ul>
        </li>
      </ul>

      <h3>What's Next</h3>
      <ul class="list">
        <li>Storage calculation optimizations.</li>
        <li>Support for Stretched Cluster sizing.</li>
        <li>VCF 9.0 sizing support.</li>
      </ul>
     `      
  },
  {
    releaseDate: "January 7th, 2026"
  },
  {
    content: `
      <h2>Private Cloud Sizer Release Notes</h2>

      <p>This release includes expanded partner access and important bug fixes to enhance the user experience.</p>

      <h3>Key Features</h3>
      <ul class="list">
        <li><strong>Partner Access:</strong> Private Cloud Sizer is now open to partners.
        </li>
        <li><strong>Bug Fixes:</strong> This release includes various bug fixes to improve stability, performance, and overall user experience of the Private Cloud Sizer tool.</li>
      </ul>

      <h3>What's Next</h3>
      <ul class="list">
        <li>Storage calculation optimizations.</li>
        <li>Support for Stretched Cluster sizing.</li>
        <li>VCF 9.0 sizing support.</li>
      </ul>
     `      
  }
]

const dec_2025 = [
  {
    releaseDate: "December 15th, 2025"
  },
  {
    content: `
      <h2>Private Cloud Sizer Release Notes</h2>

      <p>In this release, we've significantly enhanced the ESA (Express Storage Architecture) sizing algorithm to deliver faster and more accurate infrastructure recommendations for your VMware Cloud Foundation deployments.</p>

      <h3>Key Features</h3>
      <ul class="list">
        <li><strong>Smarter Sizing Calculations:</strong> Our enhanced algorithm evaluates multiple resource dimensions (CPU, memory, storage capacity, and performance) simultaneously to find the optimal cluster configuration for your workloads.</li>
        <li><strong>Enhanced Transparency:</strong> Detailed calculation logs help you understand how sizing recommendations are derived, making it easier to validate and communicate infrastructure decisions. Broadcom employees can view detailed sizing calculation logs.</li>
        <li><strong>Accessibility Improvements in the Private Cloud Sizer UI</strong></li>
      </ul>

      <h3>What is in the Next Release</h3>
      <ul class="list">
        <li>Support for Stretched Cluster sizing.</li>
        <li>Enhanced Sizing algorithm for OSA (Original Storage Architecture).</li>
        <li>VCF 9.0 sizing support will be available in subsequent releases.</li>
      </ul>
     `      
  }
]

const nov_2025 = [
  {
    releaseDate: "November 3rd, 2025"
  },
  {
    content: `
      <h2>Private Cloud Sizer Release Notes</h2>

      <p>We are excited to announce the November release of Private Cloud Sizer, featuring enhanced security and improved accessibility to provide a better experience for all users.</p>

      <h3>Key Features</h3>
      <ul class="list">
        <li><strong>Security Fixes</strong></li>
        <li><strong>Accessibility Fixes:</strong> We have implemented various accessibility improvements to ensure Private Cloud Sizer is more inclusive and usable for all users, including enhanced keyboard navigation, screen reader compatibility, and improved contrast ratios to meet accessibility standards.</li>
      </ul>
     `      
  }
]

const oct_2025 = [
  {
    releaseDate: "October 16th, 2025"
  },
  {
    content: `
      <h2>Private Cloud Sizer Release Notes</h2>

      <p>We are pleased to announce the release of Private Cloud Sizer, packed with new features, enhancements, and important bug fixes to improve your experience and expand the capabilities of the tool.</p>

      <h3>Key Features</h3>
      <ul class="list">
        <li><strong>Expanded Workload Support:</strong> Private Cloud Sizer now includes comprehensive support for VDI (Virtual Desktop Infrastructure) and DB (Database) workloads, allowing for more accurate and efficient sizing for these critical environments.</li>
        <li><strong>Auto Mode for Workload Sizing:</strong> Introducing an Option to Auto Mode for HFT (Host Failure Tolerance) and RAID (Redundant Array of Independent Disks) configurations. This new mode streamlines workload sizing in both the Management Domain and Workload Domain, simplifying the process and reducing manual configuration.</li>
        <li><strong>Bug Fixes:</strong> This release also includes a number of bug fixes to address reported issues, enhance stability, and improve overall performance of the Private Cloud Sizer tool.</li>
      </ul>
     `      
  }
]

const sep_2025 = [
  {
    releaseDate: "September 30th, 2025"
  },
  {
    content: `<h2>Private Cloud Sizer Release Notes</h2>

<p>This release introduces new features and bug fixes to the Private Cloud Sizer tool.</p>

<h3>Key Features</h3>
<ul class="list">
<li><strong>PPT Report Download:</strong>
  <ul class="list">
    <li>Sizing Recommendations can now be downloaded in PPT format.</li>
    <li>Users can initiate the report download via the newly introduced "Export to PPT" button located within the Sizing Recommendation section.</li>
    <li>The generated report provides a comprehensive overview of Sizing Recommendation information, including a Summary, Management Domain details, and Workload Domains, enabling more informed decision-making.</li>
  </ul>
</li>
<li><strong>Bug Fixes:</strong>
  <ul class="list">
    <li>The Management Domain's minimum host count is now 4, increased from 3, to align with VCF documentation.</li>
    <li>The host count per rack is updated to 16, down from 24, based on recent feedback.</li>
    <li>Operations Reserve is now enabled by default to ensure vSAN functions optimally in real-world scenarios.</li>
  </ul>
</li>
</ul>

<h3>What is in the Next Release</h3>
<ul class="list">
  <li>Support for sizing DB and VDI workloads.</li>
</ul>`
  }
];
const aug_2025 = [
  {
    releaseDate: "August 22nd, 2025"
  },
  {
    content: `<h2>Private Cloud Sizer Release Notes</h2>

<p>This release introduces the Private Cloud Sizer tool, a comprehensive solution designed to streamline the sizing of greenfield VMware Cloud Foundation (VCF) deployments. The Private Cloud Sizer tool is designed for sales teams, partners, and customers to effectively size infrastructure primarily utilizing VCF (VMware Cloud Foundation) and its associated add-ons. It provides infrastructure recommendations for both workload and management domains, supporting VCF versions starting from 5.2.1.</p>

<h3>Key Features</h3>
<ul class="list">
  <li><strong>Intuitive User Interface (UI):</strong> The Private Cloud Sizer tool provides an intuitive UI that simplifies the creation and management of sizing exercises.</li>
  <li><strong>Project Lifecycle Management:</strong> Manage the complete lifecycle of your sizing projects with functions to:
    <ul class="list">
      <li>Create: Start new sizing exercises from scratch.</li>
      <li>Delete: Remove outdated or unnecessary projects.</li>
      <li>Edit: Modify existing project configurations.</li>
      <li>Share: Collaborate on projects by sharing them as a copy or enabling real-time collaboration.</li>
    </ul>
  </li>
  <li><strong>Greenfield Deployment Sizing:</strong> The tool specifically supports the sizing of greenfield deployments utilizing VCF 5.2.1 standard architecture including sizing for:
    <ul class="list">
      <li>New VCF Management Domain deployments.</li>
      <li>One or more Workload Domains (standard and isolated).</li>
      <li>One or more general purpose workloads within a workload domain.</li>
    </ul>
  </li>
  <li><strong>Configuration Inputs:</strong> For standard greenfield sizing, users can provide detailed configurations for:
    <ul class="list">
      <li>Workload Domain: Define the specifications for your workload environments.</li>
      <li>Management Domain: Configure the parameters for your management infrastructure.</li>
      <li>Software Bill of Materials: Input information regarding the software components to be used in the deployment.</li>
    </ul>
  </li>
  <li><strong>Storage Configuration:</strong> For the supported workloads, the tool provides sizing for vSAN Storage.</li>
  <li><strong>Sizing Algorithm and Recommendations:</strong> The sizing recommendation, presented at the end of each sizing exercise, offers a thorough overview that includes:
    <ul class="list">
      <li><strong>VCF Deployment Summary:</strong> A high-level summary of the recommended VCF deployment.</li>
      <li><strong>Workload Breakdowns:</strong> Detailed information for each individual workload and its associated sizing elaborations.</li>
    </ul>
    The sizing algorithm's underlying assumptions are transparently displayed.
  </li>
  <li><strong>Additional Feature for Broadcom Employees:</strong>
    <ul class="list">
      <li><strong>Calculation Log Access:</strong> Broadcom employees with ADMIN roles can view calculation logs for management and workload domains within the Private Cloud Sizer.</li>
    </ul>
  </li>
</ul>

<h3>What is in the Next Release</h3>
<ul class="list">
  <li>Support for sizing DB and VDI workloads.</li>
  <li>Ability to recalculate sizing recommendations with user-provided adjustments to CPU, Memory, IOPS, and Capacity on the final recommendation screen.</li>
  <li>Ability to download sizer recommendations in PPT format.</li>
</ul>`
  }
];

export const releaseNote: Record<number, Record<string, ReleaseNoteEntry[]>> = {
  2026: {
    'October': oct_2026,
    'September': sep_2026,
    'August': aug_2026,
    'July': jul_2026,
    'June': [...jun_2026, ...may_2026],
    'April': apr_2026,
    'March': mar_2026,
    'January': jan_2026
  },
  2025: {
    'December': dec_2025,
    'November': nov_2025,
    'October': oct_2025,
    'September': sep_2025,
    'August': aug_2025
  }

}

/** Date entry of the newest release: the first one in the newest month of the newest year. */
export function latestRelease(notes = releaseNote): ReleaseNoteEntry | undefined {
  const years = Object.keys(notes).map(Number);
  if (!years.length) {
    return undefined;
  }
  const months = notes[Math.max(...years)];
  return months[Object.keys(months)[0]]?.find(entry => entry.releaseDate);
}
